/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { NavLink, useLocation } from "react-router-dom";

// Auth context
import { useAuth } from "context/AuthContext";
import { useUser } from "context/UserContext";
import { apiRequest } from "config/api";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Link from "@mui/material/Link";
import List from "@mui/material/List";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonInput from "components/ArgonInput";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI example components
import SidenavFooter from "examples/Sidenav/SidenavFooter";
import SidenavItem from "examples/Sidenav/SidenavItem";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Argon Dashboard 2 MUI context
import { setMiniSidenav, useArgonController } from "context";

// Logo por defecto
import logoBlack from "assets/images/black.svg";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useArgonController();
  const { miniSidenav, darkSidenav, layout } = controller;
  const location = useLocation();
  const { pathname } = location;
  const itemName = pathname.split("/").slice(1)[0];
  const { userData, loading: userLoading } = useUser();
  const [openSections, setOpenSections] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  // Función para determinar si una ruta está activa
  const isRouteActive = (routeKey, routePath) => {
    if (routePath && pathname === routePath) {
      return true;
    }

    if (pathname === "/partners/nuevo") {
      return routeKey === "crear-partner";
    }
    if (pathname === "/partnerships/nuevo") {
      return routeKey === "crear-partnership";
    }

    if (pathname.startsWith("/aliado/") && routeKey === "aliados") {
      return true;
    }
    if (pathname.startsWith("/usuario/") && routeKey === "usuarios") {
      return true;
    }
    if (pathname.startsWith("/partners/") && routeKey === "partners") {
      return true;
    }
    if (pathname.startsWith("/partnerships/") && routeKey === "partnerships") {
      return true;
    }

    return routeKey === itemName;
  };

  // Estado para el logo de la empresa
  const [empresaLogo, setEmpresaLogo] = useState(null);
  const logoDefault = logoBlack;

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

  

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

useEffect(() => {
    const cargarLogoEmpresa = async () => {
      try {
        // Usar datos del UserContext
        if (userLoading) {
          return; // Esperar a que carguen los datos
        }

        if (!userData) {
          setEmpresaLogo(logoDefault);
          return;
        }
        
        const empresaId = userData.empresaId || localStorage.getItem("empresaId");
        
        if (!empresaId) {
          setEmpresaLogo(logoDefault);
          return;
        }
        
        // Consultar el endpoint para obtener los datos de la empresa usando apiRequest
        const empresaResponse = await apiRequest(`/datos-empresa/${empresaId}`, {
          method: 'GET',
        });

        if (!empresaResponse.ok) {
          throw new Error(`HTTP error! status: ${empresaResponse.status}`);
        }

        const empresaData = await empresaResponse.json();
        
        if (empresaData && typeof empresaData.logo === 'string' && empresaData.logo.trim() !== '') {
          setEmpresaLogo(empresaData.logo);
        } else {
          setEmpresaLogo(logoDefault);
        }
      } catch (error) {
        console.log("Error al cargar logo de empresa:", error);
        setEmpresaLogo(logoDefault);
      }
    };

    cargarLogoEmpresa();
}, [userData, userLoading]);

  useEffect(() => {
    const initialSections = routes
      .filter(({ type, includeInSidebar }) => type === "title" && includeInSidebar)
      .reduce((acc, { key }) => {
        acc[key] = false;
        return acc;
      }, {});

    setOpenSections((prev) => {
      const next = { ...initialSections };

      Object.keys(prev).forEach((sectionKey) => {
        if (Object.prototype.hasOwnProperty.call(next, sectionKey)) {
          next[sectionKey] = prev[sectionKey];
        }
      });

      return next;
    });
  }, [routes]);

  const handleToggleSection = (sectionKey) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  
  const filteredRoutes = routes.filter(route => route.includeInSidebar);
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const hasActiveSearch = normalizedSearchTerm.length > 0;
  const matchesSearch = (value = "") =>
    !hasActiveSearch || value.toLowerCase().includes(normalizedSearchTerm);

  const sectionHasMatches = {};
  let sectionKeyTracker = null;

  filteredRoutes.forEach(({ type, key, name }) => {
    if (type === "title") {
      sectionKeyTracker = key;
      sectionHasMatches[key] = false;
      return;
    }

    if (type === "route" && sectionKeyTracker && matchesSearch(name)) {
      sectionHasMatches[sectionKeyTracker] = true;
    }
  });

  const firstSectionIndex = filteredRoutes.findIndex(({ type }) => type === "title");
  const sidebarEntries = [...filteredRoutes];

  if (firstSectionIndex !== -1) {
    sidebarEntries.splice(firstSectionIndex, 0, {
      type: "search",
      key: "menu-search",
    });
  }

  let currentSectionKey = null;
  const renderRoutes = sidebarEntries.map(({ type, name, icon, title, key, href, route }) => {
    let returnValue;

    if (type === "search") {
      return (
        <ArgonBox key={key} px={2} mt={2} mb={1.5}>
          <ArgonInput
            size="small"
            fullWidth
            placeholder="Buscar en el menú..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </ArgonBox>
      );
    }

    if (type === "title") {
      if (hasActiveSearch && !sectionHasMatches[key]) {
        currentSectionKey = key;
        return null;
      }

      currentSectionKey = key;
      const isOpen = Boolean(openSections[key]);

      const titleElement = (
        <ArgonBox
          key={key}
          onClick={() => handleToggleSection(key)}
          role="button"
          aria-expanded={isOpen}
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mx: 1.5,
            mt: 1.25,
            mb: 0.75,
            px: 1.5,
            py: 1,
            borderRadius: "10px",
            backgroundColor: isOpen ? "rgba(0, 0, 0, 0.05)" : "transparent",
            transition: "all 180ms ease",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.06)",
            },
          }}
        >
          <ArgonTypography
            color="dark"
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            opacity={0.75}
            letterSpacing="0.6px"
          >
            {title}
          </ArgonTypography>
          <Icon
            sx={{
              fontSize: "16px",
              opacity: 0.65,
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 180ms ease",
            }}
          >
            {isOpen ? "expand_less" : "expand_more"}
          </Icon>
        </ArgonBox>
      );

      return titleElement;
    } else if (type === "route") {
      const isBeforeFirstSection = currentSectionKey === null;

      if (!isBeforeFirstSection && !matchesSearch(name)) {
        return null;
      }

      const isInsideCollapsibleSection = currentSectionKey !== null;
      const isCurrentSectionOpen = hasActiveSearch
        ? true
        : currentSectionKey
          ? Boolean(openSections[currentSectionKey])
          : true;

      if (isInsideCollapsibleSection && !isCurrentSectionOpen) {
        return null;
      }

      if (href) {
        returnValue = (
          <Link href={href} key={key} target="_blank" rel="noreferrer">
            <SidenavItem name={name} icon={icon} active={isRouteActive(key, route)} />
          </Link>
        );
      } else {
        returnValue = (
          <NavLink to={route} key={key}>
            <SidenavItem name={name} icon={icon} active={isRouteActive(key, route)} />
          </NavLink>
        );
      }
    } else if (type === "divider") {
      returnValue = <Divider key={key} light={darkSidenav} />;
    }

    return returnValue;
  });

  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ darkSidenav, miniSidenav, layout }}>
      <ArgonBox pt={1} pb={1} px={4} textAlign="center">
        <ArgonBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <ArgonTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </ArgonTypography>
        </ArgonBox>
        <ArgonBox component={NavLink} to="/" display="flex" alignItems="center" justifyContent="flex-start" sx={{ minHeight: '46px', mb: -1.5 }}>
          <ArgonBox component="img" src={empresaLogo || logoDefault} alt="Logo Empresa" width="8.2rem" height="3.2rem" style={{ objectFit: 'contain', display: 'block' }} />
        </ArgonBox>
      </ArgonBox>
      <Divider light={darkSidenav} />
      <List>{renderRoutes}</List>

      <ArgonBox pt={1} mt="auto" mb={2} mx={2}>
        <SidenavFooter />
      </ArgonBox>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;

